// POST /v1/ai/stream for a REGISTERED capability, and the SSE frame reader.
//
// SSE never rides openapi-fetch, so this is a raw fetch authenticated through
// the module token holder, exactly like the raw-download paths. Every call
// names a capability: the body without `capability_id` falls through to the
// ungoverned raw-prompt stream (AI_RAW_PROMPT_STREAM_ENABLED, off everywhere).
//
// What a non-2xx answer MEANS differs per surface (the assistant hides itself
// on 503, the reports tab shows the envelope's message), so the request and
// the frame reader are split and each caller decides on the status in between.

import { getAccessToken, platformGraphBaseUrl } from "@/api/platform-graph";

export function aiAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  return {
    "content-type": "application/json",
    ...(token !== null && { authorization: `Bearer ${token}` }),
  };
}

export function postAiCapabilityStream(
  capabilityId: string,
  prompt: string,
  options: { readonly signal?: AbortSignal; readonly body?: Readonly<Record<string, unknown>> } = {},
): Promise<Response> {
  const init: RequestInit = {
    method: "POST",
    headers: { ...aiAuthHeaders(), accept: "text/event-stream" },
    body: JSON.stringify({ ...options.body, prompt, capability_id: capabilityId }),
  };
  if (options.signal) init.signal = options.signal;
  return fetch(`${platformGraphBaseUrl}/v1/ai/stream`, init);
}

/** Yield each `data:` frame of an SSE body as parsed JSON, in arrival order. */
export async function* readSseEvents<TEvent>(response: Response): AsyncGenerator<TEvent, void> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop() ?? "";
      for (const block of blocks) {
        const dataLine = block.split("\n").find((l) => l.startsWith("data: "));
        if (!dataLine) continue;
        yield JSON.parse(dataLine.slice("data: ".length)) as TEvent;
      }
    }
  } finally {
    reader.releaseLock();
  }
}
